import { useState, useCallback } from "react";
import { useSubscription } from "./useSubscription";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatformSettings } from "./usePlatformSettings";
import type { PlanTier } from "@/lib/tiers";

interface PromptState {
  open: boolean;
  feature: string;
  requiredTier: PlanTier;
  description?: string;
}

/**
 * Centralized tier gating: provides feature checks, usage queries, and an
 * upgrade-prompt controller. Consumers call `requireFeature` or `checkLimit`
 * before performing a gated action; if the user is below the required tier
 * (or already at a hard cap), the prompt opens and the action is blocked.
 */
export function useTierGate() {
  const { user } = useAuth();
  const { tier, limits, hasFeature, loading } = useSubscription();
  const { pricingEnabled } = usePlatformSettings();
  const [prompt, setPrompt] = useState<PromptState>({
    open: false,
    feature: "",
    requiredTier: "team",
  });

  // ---- Usage queries (only fetch when user is on Personal — cheap) ---------
  const isPersonal = pricingEnabled && tier === "personal";

  const { data: templateCount = 0, refetch: refetchTemplates } = useQuery({
    queryKey: ["usage-templates", user?.id],
    enabled: !!user?.id && isPersonal,
    queryFn: async () => {
      const { count } = await supabase
        .from("report_templates")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user!.id);
      return count ?? 0;
    },
  });

  const { data: cyclesThisMonth = 0, refetch: refetchCycles } = useQuery({
    queryKey: ["usage-cycles-month", user?.id],
    enabled: !!user?.id && isPersonal,
    queryFn: async () => {
      const start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      const { count } = await supabase
        .from("report_cycles")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .gte("created_at", start.toISOString());
      return count ?? 0;
    },
  });

  // ---- Gating helpers ------------------------------------------------------
  const showPrompt = useCallback(
    (feature: string, requiredTier: PlanTier, description?: string) => {
      setPrompt({ open: true, feature, requiredTier, description });
    },
    [],
  );

  const closePrompt = useCallback(() => {
    setPrompt((p) => ({ ...p, open: false }));
  }, []);

  /** Check if the current tier supports a feature; if not, open the prompt and return false. */
  const requireFeature = useCallback(
    (feature: string, requiredTier: PlanTier, description?: string): boolean => {
      if (!pricingEnabled) return true;
      if (hasFeature(requiredTier)) return true;
      showPrompt(feature, requiredTier, description);
      return false;
    },
    [hasFeature, showPrompt, pricingEnabled],
  );

  return {
    tier,
    limits,
    loading,
    isPersonal,
    // usage
    templateCount,
    cyclesThisMonth,
    refetchTemplates,
    refetchCycles,
    // hard caps (true = AT or OVER cap, action should be blocked)
    atTemplateCap: pricingEnabled && templateCount >= limits.maxTemplates,
    atCycleCap: pricingEnabled && cyclesThisMonth >= limits.maxCyclesPerMonth,
    // prompt
    prompt,
    showPrompt,
    closePrompt,
    requireFeature,
    hasFeature,
    pricingEnabled,
  };
}
